import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const DESKTOP_QUERY = '(min-width: 750px)';

/**
 * Inline header search. On desktop the primary navigation gives up its space and the
 * search field reveals into it; on mobile the existing search modal is used instead,
 * so this component only ever takes over above the desktop breakpoint.
 *
 * The placeholder is the input's own, fixed text (snippets/search-inline.liquid). It used to be
 * typed out in turn by this component; that animation was removed on 2026-10-01.
 */
class HeaderSearchInline extends Component {
  #controller = new AbortController();

  get #isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="q"]'));
  }

  get #header() {
    return this.closest('.header');
  }

  get #triggers() {
    return document.querySelectorAll(`[aria-controls="${this.id}"]`);
  }

  get #visibleTrigger() {
    const triggers = /** @type {HTMLElement[]} */ ([...this.#triggers]);
    return triggers.find((el) => el.offsetParent !== null) ?? triggers[0] ?? null;
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();

    // clip-path hides the closed field visually but leaves it tabbable, so keyboard
    // users would land inside a panel they cannot see.
    if (!this.isOpen) this.toggleAttribute('inert', true);

    const { signal } = this.#controller;
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Opens the inline field. The modal and this component are mutually exclusive
   * (see snippets/search-inline.liquid), so the modal is only ever there as a
   * fallback if a merchant switches the setting back mid-session.
   */
  toggle = () => {
    if (!this.#isDesktop && document.getElementById('search-modal')) {
      this.#openModal();
      return;
    }

    this.isOpen ? this.close() : this.open();
  };

  open = () => {
    if (this.isOpen) return;

    this.setAttribute('open', '');
    this.removeAttribute('inert');
    this.#header?.setAttribute('data-inline-search', 'open');
    this.#syncTriggers(true);

    const focus = () => this.#input?.focus();
    prefersReducedMotion() ? focus() : setTimeout(focus, 180);
  };

  close = () => {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    this.#header?.removeAttribute('data-inline-search');
    this.#syncTriggers(false);

    const input = this.#input;
    if (input) input.value = '';

    // Two triggers render (header + the mobile drawer duplicate) and only one is
    // visible; focusing the hidden one silently fails and strands focus inside the
    // panel. Move focus out BEFORE going inert, or inert's own focus fixup fires
    // afterwards and dumps focus on <body>.
    const trigger = this.#visibleTrigger;
    trigger?.focus();
    this.toggleAttribute('inert', true);

    // The trigger can be hidden a frame AFTER it takes focus. Closing gives the navigation its space
    // back, the overflow list re-measures, and setHeaderMenuStyle (utilities.js) swaps which of the
    // two triggers is shown inside a requestAnimationFrame. At 750 that hid the one just focused and
    // dropped focus on <body> (REGRESSIONS.md, 2026-10-01). Follow it to the trigger shown now, but
    // only when the focused one has really gone: a blur caused by the person moving on is theirs.
    trigger?.addEventListener(
      'blur',
      (event) => {
        if (event.relatedTarget || trigger.getClientRects().length > 0) return;
        requestAnimationFrame(() => {
          if (document.activeElement && document.activeElement !== document.body) return;
          this.#visibleTrigger?.focus();
        });
      },
      { once: true, signal: AbortSignal.timeout(1000) }
    );
  };

  #openModal() {
    const modal = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (
      document.getElementById('search-modal')
    );
    modal?.showDialog?.();
  }

  /**
   * @param {boolean} expanded
   */
  #syncTriggers(expanded) {
    for (const trigger of this.#triggers) {
      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.isOpen) return;

    const target = /** @type {Node} */ (event.target);
    if (this.contains(target)) return;
    for (const trigger of this.#triggers) {
      if (trigger.contains(target)) return;
    }

    this.close();
  };
}

if (!customElements.get('header-search-inline')) {
  customElements.define('header-search-inline', HeaderSearchInline);
}
